import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Typography, useMediaQuery } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryRuleService from '../../../shared/services/library-rule/library-rule.service';
import { ILibraryRule } from '../../../shared/services/library-rule/library-rule.model';
import { IPostRule } from '../../../shared/services/rule/rule.model';
import gaiaTheme from '../../../root-components/theme';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { RuleDialog } from '../../dialogs/settings/data-import/rule-dialog';
import { PartnerLibraryPushDialog } from '../../dialogs/partner/partner-library-push-dialog';
import { PartnerLibraryDeleteDialog } from '../../dialogs/partner/partner-library-delete-dialog';
import { PartnerLibraryRuleImportDialog } from '../../dialogs/partner/partner-library-rule-import-dialog';
import { LockOpenOutlined, LockOutlined, PublishedWithChangesRounded } from '@mui/icons-material';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { useDebounce } from '../../../hooks/use-debounce-hook';

// The partner's reusable rules. Authored here with the same dialog clients use for their own
// rules, then pushed into selected client accounts, where they become read-only.
export const PartnerLibraryRulesPage = () => {
  //Services
  const libraryRuleService = new LibraryRuleService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [libraryRules, setLibraryRules] = useState<ILibraryRule[]>()
  const [libraryRuleId, setLibraryRuleId] = useState<string>()
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [pushTarget, setPushTarget] = useState<{ id: string, name: string }>()
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string, pushedClientCount: number }>()
  const [errorCode, setErrorCode] = useState<number>()
  const [errorMessage, setErrorMessage] = useState<string>()
  //Filter State
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  //Table State
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(25)
  const [selected, setSelected] = useState<string[]>([]);
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  // Guards against a slow earlier request landing after a newer one, which would show stale
  // rows for the current filters.
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Use Effects
  useEffect(() => {
    getAllLibraryRules()
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  useEffect(() => {
    if (inputPage === 0) {
      setChange(c => c + 1)
    } else {
      setInputPage(0)
    }
  }, [debouncedSearchQuery, typeFilter])

  const getAllLibraryRules = () => {
    requestId.current++
    const currentRequestId = requestId.current
    libraryRuleService.getAllWithPagination(inputPage, perPage, orderBy, order, debouncedSearchQuery || undefined, typeFilter || undefined)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setLibraryRules(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: 'Action', sortable: false, includeInMobile: false },
    { id: 'name', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'value', numeric: false, label: 'Value', sortable: true, isHeading: true },
    { id: 'conversion', numeric: false, label: 'Conversion', sortable: false },
    { id: 'type', numeric: false, label: 'Type', sortable: false },
    { id: 'clients', numeric: false, label: 'Clients', sortable: false },
    { id: 'clientEditing', numeric: false, label: 'Client editing', sortable: false },
  ];

  const rows = !libraryRules ? [] : libraryRules.map((libraryRule) => {
    return {
      id: libraryRule.id,
      name: libraryRule.name,
      value: libraryRule.value,
      conversion: libraryRule.type === 'activity-based emission factor'
        ? `${libraryRule.groupName}, ${libraryRule.categoryName}, ${libraryRule.detailLevelOne}, ${libraryRule.detailLevelTwo}${libraryRule.detailLevelThree ? `, ${libraryRule.detailLevelThree}` : ''}${libraryRule.detailLevelFour ? `, ${libraryRule.detailLevelFour}` : ''}${libraryRule.detailLevelFive ? `, ${libraryRule.detailLevelFive}` : ''}, ${libraryRule.measurementUnitName}`
        : libraryRule.type === 'Vehicle Rule' ? (libraryRule.vehicleType ?? '') : '',
      type: libraryRule.type === 'activity-based emission factor' ? 'Activity-Based Conversion Factor' : libraryRule.type === 'Vehicle Rule' ? 'Vehicle Rule' : '',
      clients: (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant='body2'>
            {libraryRule.pushedClientCount === 0 ? 'Not pushed' : `${libraryRule.pushedClientCount} pushed`}
          </Typography>
          {libraryRule.outOfDateClientCount > 0 &&
            <Chip size='small' variant='outlined' color='warning' label={`${libraryRule.outOfDateClientCount} out of date`} />}
          {libraryRule.editedByClientCount > 0 &&
            <Chip size='small' variant='outlined' color='info' label={`${libraryRule.editedByClientCount} edited by client`} />}
        </Stack>
      ),
      clientEditing: (
        <Chip
          size='small'
          variant='outlined'
          color={libraryRule.allowClientEdit ? 'default' : 'success'}
          icon={libraryRule.allowClientEdit ? <LockOpenOutlined fontSize='small' /> : <LockOutlined fontSize='small' />}
          label={libraryRule.allowClientEdit ? 'Client can edit' : 'Locked'}
        />
      ),
      //Pushing is the page's main per-row action, so it gets an inline button rather
      //than hiding in the kebab (same pattern as the reduction scenarios table).
      action: (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <Button
            size='small'
            startIcon={<PublishedWithChangesRounded />}
            onClick={() => setPushTarget({ id: libraryRule.id, name: libraryRule.name })}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Push to clients
          </Button>
          <TableActionButtons
            id={libraryRule.id}
            includeEdit={true}
            includeDelete={true}
            onEdit={() => editClickHandler(libraryRule.id)}
            onDelete={() => setDeleteTarget({ id: libraryRule.id, name: libraryRule.name, pushedClientCount: libraryRule.pushedClientCount })}
            additionalMenuItems={[{
              label: libraryRule.allowClientEdit ? 'Lock for clients' : 'Allow clients to edit',
              icon: libraryRule.allowClientEdit
                ? <LockOutlined sx={{ cursor: 'pointer', mr: 1 }} />
                : <LockOpenOutlined sx={{ cursor: 'pointer', mr: 1 }} />,
              onClick: () => toggleClientEditHandler(libraryRule),
            }]}
          />
        </Stack>
      )
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const editClickHandler = (libraryRuleId: string) => {
    setLibraryRuleId(libraryRuleId)
    setOpenDialog(true)
  }

  // Turning client editing ON is harmless, so it applies straight away. Turning it OFF is
  // confirmed when clients have already changed their copies, because locking is what makes
  // a later push overwrite them.
  const toggleClientEditHandler = (libraryRule: ILibraryRule) => {
    const next = !libraryRule.allowClientEdit

    const apply = () => {
      libraryRuleService.setClientEditPermission(libraryRule.id, next)
        .then(() => {
          userNotificationsContext.openSnackbar({
            open: true,
            message: next
              ? 'Clients can now edit their copy of this rule'
              : 'This rule is now locked for clients',
          })
          getAllLibraryRules()
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          console.log(err)
        })
    }

    if (next || libraryRule.editedByClientCount === 0) {
      apply()
      return
    }

    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `${libraryRule.editedByClientCount} client${libraryRule.editedByClientCount === 1 ? ' has' : 's have'} already changed their copy of this rule. Locking it stops them changing it further. Their current version stays in place until you push over it.`,
      buttonCopy: 'Lock it',
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false
    })
      .then(response => { if (response) apply() })
      .catch(err => {
        userNotificationsContext.openSnackbar({ open: true, message: `There was an error, please try again`, backgroundColor: 'text.primary' })
        console.log(err)
      })
  }

  //Dialog Event Handlers
  const submitDialogHandler = (values: IPostRule, resetForm: () => void, isEdit: boolean,) => {
    if (isDialogSubmitting) return
    setIsDialogSubmitting(true)

    const request = isEdit && libraryRuleId
      ? libraryRuleService.edit(libraryRuleId, values)
      : libraryRuleService.create(values)

    request
      .then(() => {
        getAllLibraryRules()
        resetForm();
        setOpenDialog(false)
        setLibraryRuleId('')
        userNotificationsContext.openSnackbar({ open: true, message: isEdit ? 'Updated successfully' : 'Saved successfully' });
        setIsDialogSubmitting(false)
      })
      .catch(err => {
        if (err.response?.data?.message) {
          setErrorMessage(err.response.data.message)
          setErrorCode(err.response.data.code)
        } else {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        }
        console.log(err)
        setIsDialogSubmitting(false)
      })
  }

  const closeDialogHandler = () => {
    setOpenDialog(false)
    setLibraryRuleId('')
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title='Library'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => { setOpenDialog(true) }}
        actionButtonText='Add Rule'
        includeSecondaryActionButton={true}
        secondaryActionButtonIcon={IconName.UploadOutlined}
        secondaryActionButtonHandler={() => setIsImporting(true)}
        secondaryActionButtonText='Import from client'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        Set up rules once here, then push them to the client accounts that need them. Pushed rules are
        read-only for your clients, and pushing again updates their copy.
      </Typography>
      <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </Box>
        <FormControl size='small' sx={{ minWidth: 220 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label='Type'
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='emission-activity'>Activity-Based Conversion Factor</MenuItem>
            <MenuItem value='vehicle'>Vehicle Rule</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <LoadingPage pageReady={!isLoading}>
        {libraryRules && <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          selectByIcon={true}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />}
      </LoadingPage>

      {/* fetchRule points the shared dialog at the library endpoint; saving is already
          delegated back here through onSubmit. */}
      {!libraryRuleId && openDialog &&
        <RuleDialog
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openDialog}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandler}
          titleNoun='Library Rule'
          isEdit={false} />}
      {libraryRuleId && openDialog &&
        <RuleDialog
          isSubmitting={isDialogSubmitting}
          onSubmit={submitDialogHandler}
          errorCode={errorCode}
          errorMessage={errorMessage}
          open={openDialog}
          closeHandler={closeDialogHandler}
          isEdit={true}
          titleNoun='Library Rule'
          fetchRule={(id) => libraryRuleService.getOne(id)}
          ruleId={libraryRuleId} />}

      {pushTarget &&
        <PartnerLibraryPushDialog
          open={Boolean(pushTarget)}
          libraryRuleId={pushTarget.id}
          libraryRuleName={pushTarget.name}
          closeHandler={() => setPushTarget(undefined)}
          onPushed={getAllLibraryRules} />}

      <PartnerLibraryRuleImportDialog
        open={isImporting}
        closeHandler={() => setIsImporting(false)}
        onImported={() => { setIsImporting(false); getAllLibraryRules() }} />

      {deleteTarget &&
        <PartnerLibraryDeleteDialog
          open={Boolean(deleteTarget)}
          libraryRuleId={deleteTarget.id}
          libraryRuleName={deleteTarget.name}
          pushedClientCount={deleteTarget.pushedClientCount}
          closeHandler={() => setDeleteTarget(undefined)}
          onDeleted={getAllLibraryRules} />}
    </PageOuterContainer>
  )
}
